import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Chip,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as TemplatesIcon,
  GridOn as GridIcon,
  Warning as WarningIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { templatesApi } from '../api';
import type { BoardTemplate, CreateTemplateInput, PayoutSchemeConfig, NumberMode, CellMode, CellShape } from '../types';
import { SPORTS, DEFAULT_PAYOUT_CONFIG, getTotalCells } from '../types';
import { useDocumentTitle, usePagination } from '../hooks/core';
import { PayoutConfigEditor, TierCreditBadge } from '../components';
import { BoardTemplatesEducationModal } from '../components/education';
import { hasSeenEducationModal } from '../lib/educationModals';
import { getDefaultDollarPayouts, calculatePayoutValidation } from '../utils/payoutCalculation';
import { formatDate } from '../utils/dateFormatting';

export const TemplatesPage: React.FC = () => {
  useDocumentTitle('Board Templates');
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<BoardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { paginationProps, resetPage, getPaginatedItems } = usePagination();

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sport, setSport] = useState('football');
  const [quarters, setQuarters] = useState(4);
  const [numberMode, setNumberMode] = useState<NumberMode>('single');
  const [cellMode, setCellMode] = useState<CellMode>('standard');
  const [cellShape, setCellShape] = useState<CellShape>('1x1');
  const [pricePerSquare, setPricePerSquare] = useState(10);
  const [profitPercent, setProfitPercent] = useState(10);
  const [payoutConfig, setPayoutConfig] = useState<PayoutSchemeConfig>(() => {
    const defaultDollars = getDefaultDollarPayouts(4, 10, 10);
    return {
      ...DEFAULT_PAYOUT_CONFIG,
      totalPot: 1000,
      quarterPayouts: defaultDollars,
    };
  });
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('board_templates'));

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await templatesApi.list();
      setTemplates(data);
      setError(null);
    } catch (_err) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates by search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const query = searchQuery.toLowerCase().trim();
    return templates.filter(t =>
      t.name.toLowerCase().includes(query) ||
      (t.description && t.description.toLowerCase().includes(query)) ||
      t.sport.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // Paginated templates
  const paginatedTemplates = getPaginatedItems(filteredTemplates);

  // Handle search change (reset to first page)
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    resetPage();
  };

  // Helper to reset payout config based on quarters/price/profit/cellMode
  // preserveCustom: if true, keeps existing customCategories and other custom settings
  const resetPayoutConfig = (q: number, price: number, profit: number, mode: CellMode = 'standard', preserveCustom = false) => {
    const totalCells = getTotalCells(mode);
    const defaultDollars = getDefaultDollarPayouts(q, price, profit, mode);
    setPayoutConfig((prev) => ({
      ...DEFAULT_PAYOUT_CONFIG,
      totalPot: price * totalCells,
      quarterPayouts: defaultDollars,
      // Preserve custom categories and other per-quarter settings when changing price/quarters
      ...(preserveCustom && {
        customCategories: prev.customCategories,
        quarterWrongWayPayouts: prev.quarterWrongWayPayouts,
        quarterPlusNPayouts: prev.quarterPlusNPayouts,
        plusFiveEnabled: prev.plusFiveEnabled,
        plusNOffset: prev.plusNOffset,
        overtimeConfig: prev.overtimeConfig,
      }),
    }));
  };

  const handleOpenDialog = () => {
    setName('');
    setDescription('');
    setSport('football');
    setQuarters(4);
    setNumberMode('single');
    setCellMode('standard');
    setCellShape('1x1');
    setPricePerSquare(10);
    setProfitPercent(10);
    resetPayoutConfig(4, 10, 10, 'standard');
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setName('');
    setDescription('');
    setSport('football');
    setQuarters(4);
    setNumberMode('single');
    setCellMode('standard');
    setCellShape('1x1');
    setPricePerSquare(10);
    setProfitPercent(10);
    resetPayoutConfig(4, 10, 10, 'standard');
    setDialogError(null);
  };

  // Handle quarters change - reset payouts to match new quarter count
  // Note: changing quarters resets quarter-specific payouts but preserves custom categories
  const handleQuartersChange = (newQuarters: number) => {
    setQuarters(newQuarters);
    const totalCells = getTotalCells(cellMode);
    // Preserve custom categories when changing quarters (quarter payouts will be reset to defaults)
    const defaultDollars = getDefaultDollarPayouts(newQuarters, pricePerSquare, profitPercent, cellMode);
    setPayoutConfig((prev) => ({
      ...DEFAULT_PAYOUT_CONFIG,
      totalPot: pricePerSquare * totalCells,
      quarterPayouts: defaultDollars,
      // Preserve non-quarter-specific settings
      customCategories: prev.customCategories,
      plusNOffset: prev.plusNOffset,
    }));
  };

  // Handle price change - recalculate payouts but preserve custom categories
  const handlePriceChange = (newPrice: number) => {
    setPricePerSquare(newPrice);
    resetPayoutConfig(quarters, newPrice, profitPercent, cellMode, true);
  };

  // Handle cell mode change - reset payouts for new cell count
  const handleCellModeChange = (newCellMode: CellMode, newCellShape: CellShape) => {
    setCellMode(newCellMode);
    setCellShape(newCellShape);
    resetPayoutConfig(quarters, pricePerSquare, profitPercent, newCellMode);
    // Disable neighbor payouts when changing to non-standard cell mode
    if (newCellMode !== 'standard') {
      setPayoutConfig(prev => ({
        ...prev,
        ...(prev.boxNeighborPayouts?.enabled
          ? { boxNeighborPayouts: { ...prev.boxNeighborPayouts, enabled: false } }
          : {}),
        ...(prev.neighborPayouts?.enabled
          ? { neighborPayouts: { ...prev.neighborPayouts, enabled: false } }
          : {}),
      }));
    }
  };

  // Compute payout validation
  const payoutValidation = useMemo(
    () => calculatePayoutValidation(pricePerSquare, profitPercent, payoutConfig, quarters, cellMode),
    [pricePerSquare, profitPercent, payoutConfig, quarters, cellMode]
  );

  const handleCreate = async () => {
    if (!name.trim()) {
      setDialogError('Template name is required');
      return;
    }

    if (!payoutValidation.isValid) {
      setDialogError('Please ensure all payouts are allocated correctly before creating');
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      const totalCells = getTotalCells(cellMode);
      const input: CreateTemplateInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        sport,
        quarters,
        numberMode,
        cellMode,
        cellShape,
        pricePerSquare,
        payoutConfig: {
          ...payoutConfig,
          profitPercent,
          totalPot: pricePerSquare * totalCells,
        },
      };
      const newTemplate = await templatesApi.create(input);
      handleCloseDialog();
      // Navigate to edit the new template (assignments tab)
      navigate(`/profile/templates/${newTemplate.id}`);
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template: BoardTemplate) => {
    if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await templatesApi.delete(template.id);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const getSportLabel = (sportValue: string) => {
    return SPORTS.find(s => s.value === sportValue)?.label || sportValue;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <TemplatesIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Board Templates
            </Typography>
            <Tooltip title="Learn more about templates">
              <IconButton
                component={RouterLink}
                to="/support/boards#templates"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Create reusable templates with preset payout settings and player assignments.
            Save time by starting new boards from your templates.
          </Typography>
        </Box>
        <Tooltip title="Create Template">
          <IconButton
            color="primary"
            onClick={handleOpenDialog}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tip about saving boards as templates */}
      {templates.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> You can also save any existing board as a template from the board's <strong>Settings</strong> tab. This is a great way to reuse your favorite configurations and player assignments.
          </Typography>
        </Alert>
      )}

      {templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TemplatesIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a template to save your preferred board settings and player assignments.
            You can also save any existing board as a template from the board settings.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Create Your First Template
          </Button>
        </Paper>
      ) : (
        <>
          {/* Search field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
              {searchQuery.trim() && ` (filtered from ${templates.length})`}
            </Typography>
            <TextField
              size="small"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ ml: 'auto', minWidth: 180 }}
              InputProps={{
                sx: { height: 32 },
              }}
            />
          </Box>

          {/* Templates table */}
          {filteredTemplates.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No templates found matching "{searchQuery}"
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Template</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 100 }}>Sport</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 100 }}>Assignments</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 120 }}>Created</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontWeight: 600, width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTemplates.map((template) => (
                      <tr
                        key={template.id}
                        style={{
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/profile/templates/${template.id}`)}
                      >
                        {/* Template Name */}
                        <td style={{ padding: '12px' }}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {template.name}
                            </Typography>
                            {template.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {template.description.length > 60
                                  ? `${template.description.substring(0, 60)}...`
                                  : template.description}
                              </Typography>
                            )}
                          </Box>
                        </td>

                        {/* Sport & Tier */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Chip
                              label={getSportLabel(template.sport)}
                              size="small"
                              variant="outlined"
                            />
                            <TierCreditBadge numberMode={template.numberMode} size="small" />
                          </Box>
                        </td>

                        {/* Assignments */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <GridIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {template.squareAssignments?.length || 0}
                            </Typography>
                          </Box>
                        </td>

                        {/* Created */}
                        <td style={{ textAlign: 'center', padding: '12px' }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(template.createdAt)}
                          </Typography>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: 'center', padding: '12px' }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/profile/templates/${template.id}`);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(template);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <TablePagination
                component="div"
                count={filteredTemplates.length}
                {...paginationProps}
              />
            </>
          )}
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Board Template</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          {/* Basic Info Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
            Template Details
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
            required
            placeholder="e.g., NFL Season Template"
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 250))}
            helperText={`${description.length}/250 characters`}
            sx={{ mb: 2 }}
          />
          {/* Row 1: Sport and Quarters/Periods */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select
              label="Sport"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              sx={{ flex: 1 }}
            >
              {SPORTS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Quarters/Periods"
              value={quarters}
              onChange={(e) => handleQuartersChange(Number(e.target.value))}
              sx={{ flex: 1 }}
            >
              <MenuItem value={4}>4 Quarters</MenuItem>
              <MenuItem value={3}>3 Periods</MenuItem>
              <MenuItem value={2}>2 Halves</MenuItem>
            </TextField>
          </Box>
          {/* Row 2: Number Sets and Board Mode */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select
              label="Number Sets"
              value={numberMode}
              onChange={(e) => setNumberMode(e.target.value as NumberMode)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="single">Same for All Periods</MenuItem>
              <MenuItem value="half">Different per Half</MenuItem>
              <MenuItem value="quarter">Different per Quarter</MenuItem>
            </TextField>
            <TextField
              select
              label="Participation Mode"
              value={`${cellMode}-${cellShape}`}
              onChange={(e) => {
                const [mode, shape] = e.target.value.split('-') as [CellMode, CellShape];
                handleCellModeChange(mode, shape);
              }}
              sx={{ flex: 1 }}
            >
              <MenuItem value="standard-1x1">100 Cells (Standard)</MenuItem>
              <MenuItem value="paired-1x2">50 Cells (Horizontal Pairs)</MenuItem>
              <MenuItem value="paired-2x1">50 Cells (Vertical Pairs)</MenuItem>
              <MenuItem value="quad-2x2">25 Cells (Quad)</MenuItem>
            </TextField>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Payout Configuration Section */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Payout Configuration
          </Typography>
          <PayoutConfigEditor
            payoutConfig={payoutConfig}
            onPayoutConfigChange={setPayoutConfig}
            pricePerSquare={pricePerSquare}
            onPricePerSquareChange={handlePriceChange}
            profitPercent={profitPercent}
            onProfitPercentChange={setProfitPercent}
            quarters={quarters}
            totalCells={getTotalCells(cellMode)}
            cellMode={cellMode}
          />

          {/* Validation warning */}
          {!payoutValidation.isValid && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 2 }}>
              {payoutValidation.isOverage
                ? `Payouts exceed the payout pot by $${Math.abs(payoutValidation.remainingDollars).toFixed(2)}. Reduce payout amounts to create.`
                : `$${payoutValidation.remainingDollars.toFixed(2)} unallocated. Allocate all funds to create.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Tooltip
            title={
              !payoutValidation.isValid
                ? payoutValidation.isOverage
                  ? `Cannot create: $${Math.abs(payoutValidation.remainingDollars).toFixed(2)} over budget`
                  : `Cannot create: $${payoutValidation.remainingDollars.toFixed(2)} unallocated`
                : !name.trim()
                  ? 'Template name is required'
                  : ''
            }
          >
            <span>
              <Button
                onClick={handleCreate}
                variant="contained"
                disabled={saving || !name.trim() || !payoutValidation.isValid}
              >
                {saving ? 'Creating...' : 'Create Template'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <BoardTemplatesEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
};
